<template>
  <div class="grid-order-retries">
    <div v-if="loading" class="loader"></div>
    <div v-else>
      <div class="buttons-cnt">
        <backstage-button class="button-order" v-on:click="copyOrderNumbers">
          {{ $t('admin.copy_order_numbers') }}
        </backstage-button>
      </div>
      <ag-grid-vue
        class="ag-theme-balham"
        data-cy="grid-order-retries"
        :grid-options="gridOptions"
        :column-defs="columnDefinitions"
        :default-col-def="defaultColDef"
        :row-data="rowData"
        :pagination="true"
        :pagination-page-size="pageSize"
        :suppress-menu-hide="true"
        :modules="modules"
        v-on:first-data-rendered="onFirstDataRendered">
      </ag-grid-vue>
    </div>
  </div>
</template>

<script lang="ts">
import BackstageButton from '@/components/common/BackstageButton.vue';
import { OrdersService } from '@/services/orders.service';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { GridApi } from '@ag-grid-community/core';
import { AgGridVue } from '@ag-grid-community/vue3';
import { ManuallyRetriableOrderDto } from '@onrunning/backstage-models';
import { defineComponent } from 'vue';
import { useGrid } from '../../composables/grid';

export default defineComponent({
  name: 'grid-order-retries',
  components: {
    AgGridVue,
    BackstageButton,
  },
  setup() {
    const { defaultGridOptions } = useGrid();

    return { defaultGridOptions };
  },
  data() {
    return {
      loading: false,
      ordersService: new OrdersService(),
      columnDefinitions: new Array<any>(),
      rowData: new Array<any>(),
      gridApi: undefined as GridApi | undefined,
      gridOptions: this.defaultGridOptions(),
      modules: [ClientSideRowModelModule],
      defaultColDef: {
        resizable: true,
        filter: true,
        sortable: true,
      },
      pageSize: 20,
      rowPixelsOffset: 1,
    };
  },
  async mounted() {
    const orderRetryValidation = await this.ordersService.getRetriableOrders();

    this.loading = false;

    if (orderRetryValidation.model) {
      this.loadRetriableOrdersGrid(orderRetryValidation.model);
    }

    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    this.gridApi = undefined;
  },
  methods: {
    loadRetriableOrdersGrid(retriableOrders: ManuallyRetriableOrderDto[]) {
      const columnDefinitions = [
        {
          headerValueGetter: () => this.$t('viewOrders.items_list_columns.order_number'),
          field: 'orderNumber',
          width: '70px',
          cellStyle: { textAlign: 'left' },
        },
        {
          headerValueGetter: () => this.$t('viewOrders.orders_list_columns.order_status'),
          field: 'status',
          width: '100px',
          cellStyle: { textAlign: 'left' },
        },
        {
          headerValueGetter: () => this.$t('viewOrders.orders_list_columns.created_at'),
          field: 'createdAt',
          width: '100px',
          cellStyle: { textAlign: 'left' },
        },
        {
          headerValueGetter: () => this.$t('viewOrders.orders_list_columns.last_execution_id'),
          field: 'lastExecutionId',
          width: '130px',
          cellStyle: { textAlign: 'right' },
        },
        {
          headerValueGetter: () => this.$t('viewOrders.orders_list_columns.retry_attempts'),
          field: 'retryAttempts',
          cellStyle: { textAlign: 'left' },
        },
      ];

      this.columnDefinitions = columnDefinitions;
      this.rowData = retriableOrders;
    },
    onFirstDataRendered(params: any) {
      this.gridApi = params.api;
      params.api.sizeColumnsToFit();
    },
    handleResize() {
      this.gridApi?.sizeColumnsToFit();
    },
    copyOrderNumbers() {
      let orderNumbers = '';

      this.gridApi?.forEachNodeAfterFilter((node) => {
        orderNumbers += `${node.data.orderNumber},`;
      });

      navigator.clipboard.writeText(orderNumbers);
    },
  },
});
</script>

<style lang="scss" scoped>
.grid-order-retries {
  margin-bottom: 40px;
  .buttons-cnt {
    padding: 10px 0 30px;
    text-align: right;

    .button-order {
      justify-content: flex-end;
    }
  }
}
</style>
